/** A complete chart snapshot that can remain on screen while another timeframe loads. */
export interface TimeframeChartSnapshot<T> {
  readonly seriesKey: string;
  readonly requestKey: string;
  readonly data: T;
  readonly windowSecs: number;
}

/**
 * Keeps a rendered chart only when the pending request is another timeframe
 * of the same series. A metric, render-mode, market, or outcome-set change
 * must wait for its own data so values never cross semantic units.
 */
export function resolveTimeframeChartPresentation<T>(
  committed: TimeframeChartSnapshot<T> | null,
  target: TimeframeChartSnapshot<T>,
  targetReady: boolean,
): TimeframeChartSnapshot<T> | null {
  if (targetReady) return target;
  if (committed?.requestKey === target.requestKey) return target;
  return committed?.seriesKey === target.seriesKey ? committed : null;
}

/** True while a same-series target has not yet replaced the mounted snapshot. */
export function isTimeframeChartTargetLoading<T>(
  presentation: TimeframeChartSnapshot<T> | null,
  target: TimeframeChartSnapshot<T>,
  targetReady: boolean,
): boolean {
  return presentation !== null && !targetReady && presentation.requestKey !== target.requestKey;
}

/** Only ready targets may replace the snapshot currently shown to the viewer. */
export function shouldCommitTimeframeTarget<T>(
  committed: TimeframeChartSnapshot<T> | null,
  target: TimeframeChartSnapshot<T>,
  targetReady: boolean,
): boolean {
  return (
    targetReady &&
    (committed?.requestKey !== target.requestKey || committed.seriesKey !== target.seriesKey)
  );
}
